import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Undo2, Eraser, Download, Sparkles, Trash2 } from 'lucide-react';
import { trackEvent } from '../lib/firebase';

// Palette matching the mobile app's Mindful Doodle screen
const COLORS = [
  '#1C1C1E', // black
  '#8BAAA5', // sage teal
  '#D4A574', // sandy brown
  '#E8A0A8', // pink
  '#8FB97A', // green
  '#5D6B4A', // olive
  '#B88A5C', // tan
  '#6B6F78', // gray
];

const BRUSH_SIZES = [2, 5, 9, 14];

// Daily prompts rotate by day of the week — a gentle nudge toward expression.
const DAILY_PROMPTS = [
  'Draw the shape of your week ahead',
  'Sketch something that made you smile today',
  'Draw what calm feels like',
  'Doodle a tiny garden of good thoughts',
  'Draw a memory from your childhood',
  'Sketch what freedom looks like',
  'Draw something you\'re grateful for',
];

// Simple wellness-themed SVG templates (paths in a 0..100 viewBox).
// Tapping one stamps a faint outline onto the canvas as a tracing guide.
const INSPIRATIONS = [
  {
    id: 'waves',
    label: 'Ocean waves',
    paths: [
      'M 8 32 Q 22 20 36 32 T 64 32 T 92 32',
      'M 8 50 Q 22 38 36 50 T 64 50 T 92 50',
      'M 8 68 Q 22 56 36 68 T 64 68 T 92 68',
    ],
  },
  {
    id: 'sun',
    label: 'Morning sun',
    paths: [
      'M 50 50 m -18 0 a 18 18 0 1 0 36 0 a 18 18 0 1 0 -36 0',
      'M 50 12 L 50 22', 'M 50 78 L 50 88',
      'M 12 50 L 22 50', 'M 78 50 L 88 50',
      'M 23 23 L 30 30', 'M 70 70 L 77 77',
      'M 77 23 L 70 30', 'M 30 70 L 23 77',
    ],
  },
  {
    id: 'leaf',
    label: 'Single leaf',
    paths: [
      'M 50 14 C 28 26, 28 74, 50 86 C 72 74, 72 26, 50 14 Z',
      'M 50 14 L 50 86',
      'M 50 34 L 38 42', 'M 50 44 L 36 52', 'M 50 54 L 38 62', 'M 50 64 L 40 70',
      'M 50 34 L 62 42', 'M 50 44 L 64 52', 'M 50 54 L 62 62', 'M 50 64 L 60 70',
    ],
  },
  {
    id: 'mountain',
    label: 'Mountain trio',
    paths: [
      'M 6 80 L 28 46 L 44 62 L 60 32 L 78 54 L 94 80 Z',
      'M 28 46 L 36 55', 'M 60 32 L 68 42',
      'M 70 20 m -6 0 a 6 6 0 1 0 12 0 a 6 6 0 1 0 -12 0',
    ],
  },
  {
    id: 'flower',
    label: 'Loose flower',
    paths: [
      'M 50 50 m -8 0 a 8 8 0 1 0 16 0 a 8 8 0 1 0 -16 0',
      'M 50 22 C 40 30, 40 42, 50 42 C 60 42, 60 30, 50 22 Z',
      'M 50 78 C 40 70, 40 58, 50 58 C 60 58, 60 70, 50 78 Z',
      'M 22 50 C 30 40, 42 40, 42 50 C 42 60, 30 60, 22 50 Z',
      'M 78 50 C 70 40, 58 40, 58 50 C 58 60, 70 60, 78 50 Z',
      'M 50 88 L 50 78', 'M 46 88 Q 42 85 42 78', 'M 54 88 Q 58 85 58 78',
    ],
  },
  {
    id: 'heart',
    label: 'Little heart',
    paths: [
      'M 50 80 C 30 66, 14 52, 22 36 C 28 26, 42 26, 50 38 C 58 26, 72 26, 78 36 C 86 52, 70 66, 50 80 Z',
    ],
  },
];

export default function DoodlePage() {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [color, setColor] = useState(COLORS[0]);
  const [brushSize, setBrushSize] = useState(BRUSH_SIZES[1]);
  const [isErasing, setIsErasing] = useState(false);
  const [strokes, setStrokes] = useState([]); // history for undo (supports point-based and image-based strokes)
  const strokesRef = useRef([]); // mirror of strokes for imperative draw (avoids closure staleness)
  const currentStrokeRef = useRef(null);
  const drawingRef = useRef(false);

  // Memoized daily prompt (stable across re-renders within the same day)
  const dailyPrompt = useMemo(() => DAILY_PROMPTS[new Date().getDay()], []);

  // ── Imperative draw routine (always reads strokesRef so it's never stale) ──
  const drawAll = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    strokesRef.current.forEach((stroke) => {
      if (!stroke) return;
      if (stroke.type === 'template' && stroke.image) {
        ctx.save();
        ctx.globalAlpha = stroke.opacity ?? 0.55;
        const rect = canvas.getBoundingClientRect();
        const margin = 40;
        const size = Math.min(rect.width - margin * 2, rect.height - margin * 2);
        const x = (rect.width - size) / 2;
        const y = (rect.height - size) / 2;
        ctx.drawImage(stroke.image, x, y, size, size);
        ctx.restore();
        return;
      }
      if (!stroke.points || stroke.points.length < 1) return;
      ctx.strokeStyle = stroke.color;
      ctx.lineWidth = stroke.size;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      const [first, ...rest] = stroke.points;
      ctx.moveTo(first.x, first.y);
      if (rest.length === 0) {
        ctx.arc(first.x, first.y, stroke.size / 2, 0, Math.PI * 2);
        ctx.fillStyle = stroke.color;
        ctx.fill();
      } else {
        rest.forEach((p) => ctx.lineTo(p.x, p.y));
        ctx.stroke();
      }
    });
  }, []);

  // Keep the ref in sync with React state; redraw after any state change
  useEffect(() => {
    strokesRef.current = strokes;
    drawAll();
  }, [strokes, drawAll]);

  // Mount-only: size canvas + listen for resizes
  useEffect(() => {
    const resize = () => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return;
      const rect = container.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      drawAll();
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [drawAll]);

  // ── Pointer handling ──
  const getPointerPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startStroke = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    const pt = getPointerPos(e);
    currentStrokeRef.current = {
      color: isErasing ? '#FFFFFF' : color,
      size: isErasing ? brushSize * 3 : brushSize,
      points: [pt],
    };
    // Re-draw on canvas immediately without committing to history (faster)
    const ctx = canvasRef.current.getContext('2d');
    ctx.strokeStyle = currentStrokeRef.current.color;
    ctx.lineWidth = currentStrokeRef.current.size;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(pt.x, pt.y);
  };

  const moveStroke = (e) => {
    if (!drawingRef.current || !currentStrokeRef.current) return;
    e.preventDefault();
    const pt = getPointerPos(e);
    currentStrokeRef.current.points.push(pt);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(pt.x, pt.y);
    ctx.stroke();
  };

  const endStroke = (e) => {
    if (!drawingRef.current) return;
    if (e) e.preventDefault();
    drawingRef.current = false;
    // Capture stroke value BEFORE clearing the ref — React's state updater runs
    // asynchronously, so referencing .current in the updater would read null.
    const stroke = currentStrokeRef.current;
    currentStrokeRef.current = null;
    if (stroke) {
      setStrokes((prev) => [...prev, stroke]);
    }
  };

  const handleUndo = () => {
    setStrokes((prev) => prev.slice(0, -1));
  };

  const handleClear = () => {
    setStrokes([]);
  };

  const handleInspiration = (tpl) => {
    const svgStr = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">${tpl.paths
      .map(
        (d) => `<path d="${d}" stroke="#C9CDD4" stroke-width="1.6" fill="none" stroke-linecap="round" stroke-linejoin="round"/>`,
      )
      .join('')}</svg>`;
    const dataUrl = 'data:image/svg+xml;utf8,' + encodeURIComponent(svgStr);
    const img = new Image();
    img.onload = () => {
      setStrokes((prev) => [...prev, { type: 'template', image: img, opacity: 0.6, templateId: tpl.id }]);
    };
    img.src = dataUrl;
    trackEvent('doodle_inspiration_use', { template: tpl.id });
  };

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Export to blob for faster downloads; fallback to toDataURL
    try {
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `today-doodle-${new Date().toISOString().slice(0, 10)}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }, 'image/png');
    } catch {
      const dataUrl = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `today-doodle-${new Date().toISOString().slice(0, 10)}.png`;
      a.click();
    }
    trackEvent('doodle_download', { stroke_count: strokes.length });
  };

  return (
    <div className="p-4 sm:p-6 md:p-8 lg:p-12 max-w-5xl mx-auto animate-fade-in" data-testid="doodle-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <button onClick={() => navigate(-1)} data-testid="doodle-back"
          className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:shadow-md active:scale-95"
          style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <ArrowLeft className="w-5 h-5" style={{ color: 'var(--text)' }} />
        </button>
        <h1 className="text-xl sm:text-2xl font-bold italic" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
          Mindful Doodle
        </h1>
      </div>

      {/* Daily prompt banner — rotates by day of week */}
      <div className="mb-5 rounded-xl px-5 py-3.5 border flex items-center gap-3 animate-fade-in"
        style={{
          background: 'linear-gradient(135deg, #F1F4EE 0%, #E8EEE3 100%)',
          borderColor: '#C9D6BC',
        }}
        data-testid="doodle-daily-prompt">
        <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
          style={{ background: '#8BAAA5', color: 'white' }}>
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest font-semibold mb-0.5"
            style={{ color: '#5D6B4A', fontFamily: 'Manrope' }}>
            Today's muse
          </p>
          <p className="text-sm sm:text-base italic font-medium truncate"
            style={{ color: 'var(--text)', fontFamily: 'Manrope' }}>
            "{dailyPrompt}"
          </p>
        </div>
      </div>

      {/* Canvas card */}
      <div className="rounded-2xl p-5 sm:p-6 border shadow-sm"
        style={{ background: '#FFFFFF', borderColor: 'var(--border)' }}>
        <div className="text-center mb-4">
          <h2 className="text-2xl font-bold italic mb-1" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
            Mindful Doodle
          </h2>
          <p className="text-sm italic" style={{ color: 'var(--muted-fg)' }}>Express yourself freely</p>
        </div>

        {/* Canvas */}
        <div className="relative mx-auto" style={{ maxWidth: '640px' }}>
          {/* Dotted border decoration (like the mobile's left edge) */}
          <div className="absolute top-0 left-0 h-full w-px hidden sm:block"
            style={{ borderLeft: '2px dashed var(--border)', marginLeft: '-12px' }} />
          <div ref={containerRef}
            className="relative rounded-lg overflow-hidden touch-none select-none"
            style={{
              background: '#FFFFFF',
              border: '1px solid var(--border)',
              aspectRatio: '4 / 5',
              cursor: isErasing ? 'cell' : 'crosshair',
            }}>
            <canvas ref={canvasRef}
              data-testid="doodle-canvas"
              onMouseDown={startStroke}
              onMouseMove={moveStroke}
              onMouseUp={endStroke}
              onMouseLeave={endStroke}
              onTouchStart={startStroke}
              onTouchMove={moveStroke}
              onTouchEnd={endStroke}
              onTouchCancel={endStroke}
              className="absolute inset-0 w-full h-full"
            />
          </div>
        </div>

        {/* Colors */}
        <div className="mt-6">
          <p className="text-sm mb-3" style={{ color: 'var(--muted-fg)', fontFamily: 'Manrope' }}>Colors</p>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {COLORS.map((c) => {
              const active = color === c && !isErasing;
              return (
                <button key={c} onClick={() => { setColor(c); setIsErasing(false); }}
                  data-testid={`doodle-color-${c.replace('#', '')}`}
                  aria-label={`Select ${c}`}
                  className="w-10 h-10 sm:w-11 sm:h-11 rounded-full transition-all active:scale-95"
                  style={{
                    background: c,
                    boxShadow: active ? '0 0 0 3px white, 0 0 0 5px var(--primary)' : 'inset 0 0 0 1px rgba(0,0,0,0.08)',
                    transform: active ? 'scale(1.05)' : 'scale(1)',
                  }} />
              );
            })}
          </div>
        </div>

        {/* Brushes */}
        <div className="mt-5">
          <p className="text-sm mb-3" style={{ color: 'var(--muted-fg)', fontFamily: 'Manrope' }}>Brush</p>
          <div className="flex gap-3 flex-wrap">
            {BRUSH_SIZES.map((sz) => {
              const active = brushSize === sz;
              return (
                <button key={sz} onClick={() => setBrushSize(sz)}
                  data-testid={`doodle-brush-${sz}`}
                  aria-label={`Brush size ${sz}`}
                  className="w-12 h-12 rounded-full flex items-center justify-center transition-all active:scale-95"
                  style={{
                    background: active ? '#E8F0EE' : 'var(--surface)',
                    border: `2px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
                  }}>
                  <span className="rounded-full block" style={{ width: `${sz * 1.4}px`, height: `${sz * 1.4}px`, background: isErasing ? '#CBD5E1' : color }} />
                </button>
              );
            })}
          </div>
        </div>

        {/* Action row */}
        <div className="mt-6 flex items-center gap-3 flex-wrap">
          <button onClick={handleUndo} disabled={strokes.length === 0} data-testid="doodle-undo"
            aria-label="Undo"
            className="w-12 h-12 rounded-full flex items-center justify-center transition-all active:scale-95 disabled:opacity-40"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }}>
            <Undo2 className="w-5 h-5" />
          </button>
          <button onClick={() => setIsErasing((v) => !v)} data-testid="doodle-eraser"
            aria-label="Eraser"
            className="w-12 h-12 rounded-full flex items-center justify-center transition-all active:scale-95"
            style={{
              background: isErasing ? '#E8F0EE' : 'var(--surface)',
              border: `2px solid ${isErasing ? 'var(--primary)' : 'var(--border)'}`,
              color: 'var(--text)',
            }}>
            <Eraser className="w-5 h-5" />
          </button>
          <button onClick={handleClear} disabled={strokes.length === 0} data-testid="doodle-clear"
            aria-label="Clear canvas"
            className="w-12 h-12 rounded-full flex items-center justify-center transition-all active:scale-95 disabled:opacity-40"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: '#B84A4A' }}>
            <Trash2 className="w-5 h-5" />
          </button>
          <button onClick={handleDownload} data-testid="doodle-download"
            disabled={strokes.length === 0}
            className="flex-1 min-w-[200px] sm:flex-initial sm:ml-auto px-6 py-3 rounded-full font-semibold transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ background: '#8BAAA5', color: '#FFFFFF' }}>
            <Download className="w-5 h-5" />
            Download
          </button>
        </div>
      </div>

      {/* Inspiration strip — tap a template to stamp a faint outline as a tracing guide */}
      <div className="mt-6 rounded-2xl p-5 sm:p-6 border animate-fade-in"
        style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
        data-testid="doodle-inspiration-strip">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4" style={{ color: '#8BAAA5' }} />
          <p className="text-xs font-semibold uppercase tracking-widest"
            style={{ color: 'var(--muted-fg)', fontFamily: 'Manrope' }}>
            Need a spark? Trace one
          </p>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {INSPIRATIONS.map((tpl) => (
            <button key={tpl.id} onClick={() => handleInspiration(tpl)}
              data-testid={`doodle-inspo-${tpl.id}`}
              className="flex flex-col items-center gap-1.5 p-3 rounded-xl border transition-all hover:-translate-y-0.5 hover:shadow-md active:scale-95"
              style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}>
              <svg viewBox="0 0 100 100" className="w-14 h-14" aria-hidden="true">
                {tpl.paths.map((d, i) => (
                  <path key={i} d={d} stroke="#5D6B4A" strokeWidth="2" fill="none"
                    strokeLinecap="round" strokeLinejoin="round" />
                ))}
              </svg>
              <span className="text-[11px] font-medium" style={{ color: 'var(--text)', fontFamily: 'Manrope' }}>
                {tpl.label}
              </span>
            </button>
          ))}
        </div>
        <p className="mt-4 text-xs text-center" style={{ color: 'var(--muted-fg)' }}>
          Tap any doodle to drop a faint outline on your canvas. Trace, remix, or ignore — it's your space.
        </p>
      </div>
    </div>
  );
}
